import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, Star } from "lucide-react";
import api, { mediaUrl, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/common";

export default function SiteSettingsPage() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [courses, setCourses] = useState([]);

  const load = async () => {
    try {
      const [r, c] = await Promise.all([
        api.get("/public/site-settings"),
        api.get("/public/courses"),
      ]);
      setS(r.data);
      setCourses(Array.isArray(c.data) ? c.data : []);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const upd = (k, v) => setS({ ...s, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/site-settings", s);
      setS(r.data); toast.success("Site settings saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const uploadHero = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); e.target.value = ""; return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/admin/site-settings/hero-image", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setS({ ...s, hero_images: r.data.hero_images });
      toast.success("Hero image added");
    } catch (err) { toast.error(errMsg(err)); } finally {
      setUploading(false); e.target.value = "";
    }
  };
  const removeHero = async (url) => {
    if (!window.confirm("Remove this hero image?")) return;
    try {
      const r = await api.delete("/admin/site-settings/hero-image", { params: { url } });
      setS({ ...s, hero_images: r.data.hero_images });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addFaq = () => setS({ ...s, faqs: [...(s.faqs || []), { q: "", a: "" }] });
  const updFaq = (i, k, v) => {
    const faqs = [...s.faqs]; faqs[i] = { ...faqs[i], [k]: v };
    setS({ ...s, faqs });
  };
  const rmFaq = (i) => setS({ ...s, faqs: s.faqs.filter((_, idx) => idx !== i) });

  if (!s) return <div className="p-8"><Spinner /></div>;
  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6" data-testid="site-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold">Site Settings</h1>
          <p className="text-sm text-muted-foreground">Edit the address, contact info, socials, hours, hero images and FAQs shown on your public website.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-site-settings">
          <Save className="h-4 w-4 mr-1.5" />{saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <Section title="Business identity">
        <Field label="Business name" v={s.business_name} on={(v) => upd("business_name", v)} />
        <Field label="Tagline" v={s.tagline} on={(v) => upd("tagline", v)} />
      </Section>

      <Section title="Contact">
        <Field label="Address line 1" v={s.address_line1} on={(v) => upd("address_line1", v)} />
        <Field label="Address line 2" v={s.address_line2} on={(v) => upd("address_line2", v)} />
        <Field label="Phone" v={s.phone} on={(v) => upd("phone", v)} />
        <Field label="Email" v={s.email} on={(v) => upd("email", v)} />
        <Field label="WhatsApp number (with country code)" v={s.whatsapp} on={(v) => upd("whatsapp", v)} placeholder="60115550982" />
      </Section>

      <Section title="Opening hours">
        <Field label="Monday – Friday" v={s.hours_mon_fri} on={(v) => upd("hours_mon_fri", v)} />
        <Field label="Saturday" v={s.hours_sat} on={(v) => upd("hours_sat", v)} />
        <Field label="Sunday" v={s.hours_sun} on={(v) => upd("hours_sun", v)} />
        <Field label="Public Holiday" v={s.hours_ph} on={(v) => upd("hours_ph", v)} />
      </Section>

      <Section title="Social media links (full URLs)">
        <Field label="Facebook" v={s.facebook_url} on={(v) => upd("facebook_url", v)} placeholder="https://facebook.com/cornerstonemusic" />
        <Field label="Instagram" v={s.instagram_url} on={(v) => upd("instagram_url", v)} placeholder="https://instagram.com/cornerstonemusic" />
        <Field label="TikTok" v={s.tiktok_url} on={(v) => upd("tiktok_url", v)} placeholder="https://tiktok.com/@cornerstonemusic" />
        <Field label="YouTube" v={s.youtube_url} on={(v) => upd("youtube_url", v)} placeholder="https://youtube.com/@cornerstonemusic" />
      </Section>

      <Section title={`Hero slideshow (${(s.hero_images || []).length} images)`}>
        <p className="text-sm text-muted-foreground -mt-1 mb-2">Add 1–5 images that rotate at the top of your homepage. Recommended 1600×1200, under 5 MB.</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {(s.hero_images || []).map((im) => (
            <div key={im.url} className="relative rounded-xl overflow-hidden border bg-slate-50 aspect-[4/3]">
              <img src={mediaUrl(im.url)} alt={im.alt || "Hero"} className="h-full w-full object-cover" />
              <button onClick={() => removeHero(im.url)} type="button"
                      className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 text-rose-700 flex items-center justify-center shadow"
                      data-testid={`remove-hero-${im.url.split('/').pop()}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <label className="rounded-xl border-2 border-dashed border-muted-foreground/30 aspect-[4/3] flex flex-col items-center justify-center text-sm text-muted-foreground cursor-pointer hover:bg-muted/50">
            <input type="file" accept="image/*" className="hidden" onChange={uploadHero}
                   disabled={uploading} data-testid="upload-hero" />
            <Plus className="h-5 w-5 mb-1" />
            {uploading ? "Uploading…" : "Add hero image"}
          </label>
        </div>
      </Section>

      <Section title="Location map (Google Maps embed)">
        <p className="text-sm text-muted-foreground -mt-1 mb-2">
          On Google Maps → search your school → <strong>Share → Embed a map</strong> → copy the HTML.
          Paste the full <code className="text-xs bg-slate-100 px-1 rounded">&lt;iframe …&gt;</code> tag <em>or</em> just the URL inside <code className="text-xs bg-slate-100 px-1 rounded">src=&quot;…&quot;</code>.
          We accept only <code className="text-xs bg-slate-100 px-1 rounded">google.com/maps</code> URLs for safety.
        </p>
        <label className="block">
          <span className="text-xs font-medium text-muted-foreground">Google Maps embed (URL or full iframe)</span>
          <Textarea rows={3} value={s.map_embed_url || ""}
                    onChange={(e) => upd("map_embed_url", e.target.value)}
                    placeholder='<iframe src="https://www.google.com/maps/embed?pb=…" …></iframe>'
                    className="mt-1 font-mono text-xs"
                    data-testid="map-embed-input" />
        </label>
        {s.map_embed_url && s.map_embed_url.startsWith("https://") && (
          <div className="mt-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">Preview</p>
            <div className="rounded-lg overflow-hidden border aspect-[16/9] bg-slate-50">
              <iframe title="Map preview" src={s.map_embed_url}
                      loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                      className="h-full w-full" data-testid="map-embed-preview" />
            </div>
          </div>
        )}
      </Section>

      <Section title={`Featured courses on homepage (${(s.featured_course_ids || []).length} selected)`}>
        <p className="text-sm text-muted-foreground -mt-1 mb-2">
          Tick the courses to feature on the homepage grid. If nothing is selected, the homepage falls back to a default showcase.
          Only <strong>public</strong> courses appear here — publish them from Music Courses first.
        </p>
        {courses.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No public courses available. Publish courses from Music Courses → Bulk actions.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {courses.map((c) => {
              const checked = (s.featured_course_ids || []).includes(c.id);
              return (
                <label key={c.id}
                       className={"flex items-center gap-3 rounded-lg border p-2.5 cursor-pointer transition-colors " +
                         (checked ? "border-purple-400 bg-purple-50" : "border-slate-200 hover:bg-slate-50")}
                       data-testid={`featured-course-toggle-${c.id}`}>
                  <input type="checkbox" checked={checked}
                         onChange={(e) => {
                           const cur = s.featured_course_ids || [];
                           upd("featured_course_ids", e.target.checked
                             ? [...cur, c.id]
                             : cur.filter((x) => x !== c.id));
                         }}
                         className="h-4 w-4 accent-purple-700" />
                  {c.image_url ? (
                    <img src={mediaUrl(c.image_url)} alt="" className="h-10 w-10 rounded-md object-cover shrink-0" />
                  ) : (
                    <div className="h-10 w-10 rounded-md bg-slate-100 flex items-center justify-center shrink-0">
                      <Star className="h-4 w-4 text-slate-400" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{c.name}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{c.category || "Course"}{c.level_count > 1 ? ` · ${c.level_count} levels` : ""}</p>
                  </div>
                </label>
              );
            })}
          </div>
        )}
      </Section>

      <Section title={`FAQs (${(s.faqs || []).length})`}>
        {(s.faqs || []).map((f, i) => (
          <div key={i} className="rounded-lg border p-3 mb-2 bg-muted/20" data-testid={`faq-${i}`}>
            <Input value={f.q} onChange={(e) => updFaq(i, "q", e.target.value)}
                   placeholder="Question" className="mb-2 font-medium" />
            <Textarea value={f.a} onChange={(e) => updFaq(i, "a", e.target.value)}
                      placeholder="Answer" rows={2} />
            <button type="button" onClick={() => rmFaq(i)}
                    className="mt-1 text-xs text-rose-600 hover:underline"
                    data-testid={`remove-faq-${i}`}>Remove</button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addFaq} data-testid="add-faq">
          <Plus className="h-4 w-4 mr-1" />Add FAQ
        </Button>
      </Section>

      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={saving} size="lg" data-testid="save-site-settings-bottom">
          <Save className="h-4 w-4 mr-1.5" />{saving ? "Saving…" : "Save all changes"}
        </Button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-xl border bg-white p-5 space-y-3">
      <h2 className="font-display text-base font-semibold">{title}</h2>
      {children}
    </div>
  );
}
function Field({ label, v, on, placeholder }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <Input value={v || ""} onChange={(e) => on(e.target.value)} placeholder={placeholder} className="mt-1" />
    </label>
  );
}
